import React, { useState, useRef, useCallback } from 'react';
import { SAMPLE_TEXTS, FONT_SIZE, LINE_HEIGHT, MAX_WIDTH } from '../config';

interface MeasureResult {
  text: string;
  height: number;
  lineCount: number;
  durationMs: number;
  reflowsTriggered: number;
}

export default function DomPage() {
  const [results, setResults] = useState<MeasureResult[]>([]);
  const [totalMs, setTotalMs] = useState<number | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const measurerRef = useRef<HTMLDivElement>(null);

  const measure = useCallback(() => {
    setIsRunning(true);
    setResults([]);
    setTotalMs(null);

    // We need a real DOM element to measure against
    const el = measurerRef.current;
    if (!el) return;

    el.style.font = `${FONT_SIZE}px sans-serif`;
    el.style.lineHeight = `${LINE_HEIGHT}px`;
    el.style.maxWidth = `${MAX_WIDTH}px`;
    el.style.width = `${MAX_WIDTH}px`;
    el.style.wordBreak = 'break-word';
    el.style.whiteSpace = 'pre-wrap';
    el.style.position = 'absolute';
    el.style.visibility = 'hidden';
    el.style.top = '-9999px';

    const allResults: MeasureResult[] = [];
    const overallStart = performance.now();
    let totalReflows = 0;

    for (const text of SAMPLE_TEXTS) {
      const start = performance.now();

      // Set text and trigger layout reflow to read measurements
      el.textContent = text;

      // This call triggers a layout reflow (browser must recalculate layout)
      const height = el.getBoundingClientRect().height;  // REFLOW #1
      const lineCount = Math.round(height / LINE_HEIGHT);

      totalReflows++;
      const end = performance.now();

      allResults.push({
        text,
        height,
        lineCount,
        durationMs: end - start,
        reflowsTriggered: 1,
      });
    }

    const overallEnd = performance.now();
    setResults(allResults);
    setTotalMs(overallEnd - overallStart);
    setIsRunning(false);

    console.log(`DOM approach: ${totalReflows} reflows triggered for ${SAMPLE_TEXTS.length} texts`);
  }, []);

  return (
    <div className="page">
      {/* Hidden DOM measurer element */}
      <div ref={measurerRef} aria-hidden="true" style={{ pointerEvents: 'none' }} />

      <div className="page-header dom-header">
        <h2>📐 Traditional DOM Measurement</h2>
        <p>
          Uses <code>getBoundingClientRect()</code> on a hidden element. Each measurement
          forces the browser to perform a <strong>layout reflow</strong> — one of the most
          expensive browser operations.
        </p>
        <div className="how-it-works">
          <h4>How it works:</h4>
          <ol>
            <li>Create a hidden <code>&lt;div&gt;</code> off-screen</li>
            <li>Set text content + CSS styles</li>
            <li>Call <code>getBoundingClientRect()</code> → triggers layout reflow</li>
            <li>Read <code>.height</code> from the result</li>
          </ol>
        </div>
        <div className="warning-box">
          ⚠️ <strong>The problem:</strong> Every call to <code>getBoundingClientRect</code>,{' '}
          <code>offsetHeight</code>, <code>scrollHeight</code> etc. forces the browser to flush
          its pending style calculations and recompute layout for the entire document.
          In a large app with many items (virtual lists, chat messages), this causes serious
          performance bottlenecks.
        </div>
      </div>

      <button
        className="run-btn dom-btn"
        onClick={measure}
        disabled={isRunning}
      >
        {isRunning ? '⏳ Measuring...' : '▶ Run DOM Measurement'}
      </button>

      {totalMs !== null && (
        <div className="summary dom-summary">
          <span>✅ Measured {results.length} texts</span>
          <span>⏱ Total time: <strong>{totalMs.toFixed(2)}ms</strong></span>
          <span>🔄 Layout reflows: <strong>{results.length}</strong></span>
        </div>
      )}

      {results.length > 0 && (
        <div className="results">
          {results.map((r, i) => (
            <div key={i} className="result-card">
              <div className="result-meta">
                <span className="badge dom-badge">Reflow triggered</span>
                <span className="duration">{r.durationMs.toFixed(3)}ms</span>
              </div>
              <p className="result-text">{r.text.slice(0, 80)}{r.text.length > 80 ? '…' : ''}</p>
              <div className="result-stats">
                <span>📏 Height: <strong>{r.height.toFixed(1)}px</strong></span>
                <span>↕ Lines: <strong>{r.lineCount}</strong></span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
